import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../Contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUser, faEnvelope, faPhone, faMapMarkerAlt, 
    faBriefcase, faBuilding, faLock, faCamera, 
    faSave, faTimes, faPenToSquare, faIdCard, faBook, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import '../styles/clientInvoice.css'; // Reusing some premium card styles

export default function Profile() {
    const { user, refreshUser } = useAuth();
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        bio: '',
        skills: '',
        address: '',
        state: '',
        pincode: '',
        gender: '',
        official_telephony_number: '',
        password: '' // For password change
    });

    useEffect(() => {
        if (user) {
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                phone_number: user.phone_number || '',
                bio: user.bio || '',
                skills: user.skills || '',
                address: user.address || '',
                state: user.state || '',
                pincode: user.pincode || '',
                gender: user.gender || '',
                official_telephony_number: user.official_telephony_number || '',
                password: ''
            });
        }
    }, [user]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = { ...formData };
            if (!payload.password) delete payload.password;

            await api.patch(`accounts/users/${user.id}/`, payload);
            await refreshUser();
            setEditing(false);
            toast.success("Profile updated successfully!");
        } catch (error) {
            console.error("Update error", error);
            toast.error("Failed to update profile.");
        } finally {
            setLoading(false);
        }
    };

    if (!user) return <div className="ci-root">Loading Profile...</div>;

    return (
        <div className="ci-root" style={{ padding: '2rem', maxWidth: '1000px', margin: '0 auto' }}>
            <div className="ci-header" style={{ marginBottom: '2rem' }}>
                <div className="ci-header-left">
                    <span className="ci-eyebrow">Account Settings</span>
                    <h1>Personal Workspace</h1>
                    <p>Manage your identity, contact information, and security settings.</p>
                </div>
                {!editing ? (
                    <button className="ci-btn-primary" onClick={() => setEditing(true)}>
                        <FontAwesomeIcon icon={faPenToSquare} style={{ marginRight: '8px' }} /> Edit Profile
                    </button>
                ) : (
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button className="ci-btn-secondary" onClick={() => setEditing(false)}>Cancel</button>
                        <button className="ci-btn-primary" onClick={handleSave} disabled={loading}>
                            <FontAwesomeIcon icon={faSave} style={{ marginRight: '8px' }} /> {loading ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2rem' }}>
                {/* Profile Card */}
                <div className="ci-card" style={{ height: 'fit-content', textAlign: 'center', padding: '2rem' }}>
                    <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto 1.5rem', borderRadius: '50%', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '3rem', fontWeight: 800 }}>
                        {user.first_name ? user.first_name[0].toUpperCase() : user.username[0].toUpperCase()}
                    </div>
                    <h3 style={{ marginBottom: '0.25rem' }}>{user.first_name} {user.last_name}</h3>
                    <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1rem' }}>@{user.username}</p>
                    <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '20px', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>
                        {user.rbac_roles_details?.[0]?.name || 'Member'}
                    </div>

                    <div style={{ marginTop: '2rem', textAlign: 'left', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: '#475569', fontSize: '0.9rem' }}>
                            <FontAwesomeIcon icon={faIdCard} style={{ width: '16px' }} />
                            <span>ID: {user.employee_id || 'System User'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', color: '#475569', fontSize: '0.9rem' }}>
                            <FontAwesomeIcon icon={faBuilding} style={{ width: '16px' }} />
                            <span>{user.organization_details?.name || 'Global'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#475569', fontSize: '0.9rem' }}>
                            <FontAwesomeIcon icon={faBriefcase} style={{ width: '16px' }} />
                            <span>{user.designation || 'Specialist'}</span>
                        </div>
                    </div>
                </div>

                {/* Edit Form */}
                <div className="ci-card">
                    <form onSubmit={handleSave}>
                        <div style={{ marginBottom: '2rem' }}>
                            <h3 className="ci-card-title">Personal Details</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                                <div className="ci-field">
                                    <label className="ci-label">First Name</label>
                                    <input name="first_name" disabled={!editing} value={formData.first_name} onChange={handleChange} className="ci-input" />
                                </div>
                                <div className="ci-field">
                                    <label className="ci-label">Last Name</label>
                                    <input name="last_name" disabled={!editing} value={formData.last_name} onChange={handleChange} className="ci-input" />
                                </div>
                                <div className="ci-field">
                                    <label className="ci-label">Email Address</label>
                                    <input name="email" disabled value={user.email || ''} className="ci-input" style={{ backgroundColor: '#f8fafc' }} />
                                    <small style={{ color: '#94a3b8', marginTop: '4px', display: 'block' }}>Email cannot be changed manually.</small>
                                </div>
                                <div className="ci-field">
                                    <label className="ci-label">Phone Number</label>
                                    <input name="phone_number" disabled={!editing} value={formData.phone_number} onChange={handleChange} className="ci-input" placeholder="+91 ..." />
                                </div>
                                <div className="ci-field">
                                    <label className="ci-label">Official Telephony Number (Callyzer)</label>
                                    <input name="official_telephony_number" disabled={!editing} value={formData.official_telephony_number} onChange={handleChange} className="ci-input" placeholder="e.g. 919876543210" />
                                    <small style={{ color: '#6366f1', fontSize: '0.75rem' }}>Used for linking your phone calls to this account.</small>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem', borderTop: '1px solid #f1f5f9', paddingTop: '2rem' }}>
                            <h3 className="ci-card-title">Bio & Skills</h3>
                            <div style={{ marginTop: '1rem' }}>
                                <div className="ci-field">
                                    <label className="ci-label">Professional Summary</label>
                                    <textarea name="bio" disabled={!editing} value={formData.bio} onChange={handleChange} className="ci-input" style={{ minHeight: '100px' }} />
                                </div>
                                <div className="ci-field" style={{ marginTop: '1rem' }}>
                                    <label className="ci-label">Skills (Comma Separated)</label>
                                    <input name="skills" disabled={!editing} value={formData.skills} onChange={handleChange} className="ci-input" placeholder="React, Python, Management..." />
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem', borderTop: '1px solid #f1f5f9', paddingTop: '2rem' }}>
                            <h3 className="ci-card-title">Location & Address</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                                <div className="ci-field">
                                    <label className="ci-label">Street Address</label>
                                    <input name="address" disabled={!editing} value={formData.address} onChange={handleChange} className="ci-input" />
                                </div>
                                <div className="ci-field">
                                    <label className="ci-label">State</label>
                                    <input name="state" disabled={!editing} value={formData.state} onChange={handleChange} className="ci-input" />
                                </div>
                                <div className="ci-field">
                                    <label className="ci-label">Pincode</label>
                                    <input name="pincode" disabled={!editing} value={formData.pincode} onChange={handleChange} className="ci-input" />
                                </div>
                            </div>
                        </div>

                        {editing && (
                            <div style={{ borderTop: '1px solid #fee2e2', paddingTop: '2rem', background: '#fef2f2', margin: '0 -2rem -2rem', padding: '2rem', borderRadius: '0 0 12px 12px' }}>
                                <h3 className="ci-card-title" style={{ color: '#991b1b' }}>
                                    <FontAwesomeIcon icon={faLock} style={{ marginRight: '8px' }} /> Security Settings
                                </h3>
                                <p style={{ fontSize: '0.85rem', color: '#b91c1c', marginBottom: '1rem' }}>Leave blank if you do not wish to change your password.</p>
                                <div className="ci-field" style={{ maxWidth: '400px' }}>
                                    <label className="ci-label">New Password</label>
                                    <input type="password" name="password" value={formData.password} onChange={handleChange} className="ci-input" placeholder="Enter new password..." />
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>

        </div>
    );
}
